// Los catálogos: un archivo por pantalla en packages/i18n/messages/<idioma>/*.json, fusionados aquí.
import base from "../../../packages/i18n/messages/es/base.json";

export const IDIOMAS = ["es", "pt", "en", "fr", "de", "it", "nl", "pl", "ro", "ru", "uk", "tr", "ar", "hi", "bn", "id", "ja", "ko", "th", "vi"] as const;
export type Idioma = (typeof IDIOMAS)[number];

// Un trozo por idioma (src/catalogos): el navegador solo descarga el idioma que muestra.
// Las mismas veinte lenguas que la línea de teléfono (tel/idiomas.py), desde el 18/10.
const TROZOS = import.meta.glob<{ default: Record<string, string> }>("./catalogos/*.ts");
const CARGAR = Object.fromEntries(
  IDIOMAS.map((l) => [l, TROZOS[`./catalogos/${l}.ts`]]),
) as Record<Idioma, () => Promise<{ default: Record<string, string> }>>;
const cargados: Partial<Record<Idioma, Record<string, string>>> = {};

export async function cargarCatalogo(idioma: Idioma): Promise<void> {
  cargados[idioma] ??= (await CARGAR[idioma]()).default;
}

// Solo después de cargarCatalogo(idioma): main.tsx espera el idioma inicial antes de pintar.
export function mensajes(idioma: Idioma): Record<string, string> {
  return cargados[idioma] ?? {};
}

// Las claves conocidas vienen del catálogo fuente (español) de todas las pantallas.
export type Clave = keyof typeof base | (string & { readonly __clave?: never });

// Etiqueta de BCP 47 que usa Intl para fechas y números.
export const LOCALE: Record<Idioma, string> = {
  es: "es", pt: "pt-BR", en: "en", fr: "fr", de: "de", it: "it", nl: "nl", pl: "pl", ro: "ro", ru: "ru",
  uk: "uk", tr: "tr", ar: "ar", hi: "hi", bn: "bn", id: "id", ja: "ja", ko: "ko", th: "th", vi: "vi",
};

// El árabe se lee de derecha a izquierda: toda la página se voltea, no solo el texto.
export const DIRECCION = (idioma: Idioma): "rtl" | "ltr" => (idioma === "ar" ? "rtl" : "ltr");

// Nombre de cada idioma en su propia lengua: nunca se traduce.
export const NOMBRE_IDIOMA: Record<Idioma, string> = {
  es: "Español", pt: "Português", en: "English", fr: "Français", de: "Deutsch", it: "Italiano",
  nl: "Nederlands", pl: "Polski", ro: "Română", ru: "Русский", uk: "Українська", tr: "Türkçe",
  ar: "العربية", hi: "हिन्दी", bn: "বাংলা", id: "Bahasa Indonesia", ja: "日本語", ko: "한국어",
  th: "ไทย", vi: "Tiếng Việt",
};

const CLAVE = "responia.idioma";

// Preferencia guardada, luego el navegador, luego español (idioma fuente).
export function idiomaInicial(): Idioma {
  try {
    const guardado = localStorage.getItem(CLAVE);
    if (guardado && (IDIOMAS as readonly string[]).includes(guardado)) return guardado as Idioma;
  } catch {}
  for (const l of navigator.languages ?? [navigator.language]) {
    const b = l.slice(0, 2).toLowerCase();
    if ((IDIOMAS as readonly string[]).includes(b)) return b as Idioma;
  }
  return "es";
}

export function guardarIdioma(idioma: Idioma) {
  try { localStorage.setItem(CLAVE, idioma); } catch {}
  aplicarIdioma(idioma);
}

// lang y dir del documento: el lector de pantalla, las fuentes del sistema y el sentido de lectura.
export function aplicarIdioma(idioma: Idioma) {
  document.documentElement.lang = LOCALE[idioma];
  document.documentElement.dir = DIRECCION(idioma);
}
